import api from './api';
import firstPartyAnalytics from './firstPartyAnalytics';
import { AnalyticsEvents } from './analyticsEvents';
import type { FollowListProfile, FollowListResponse } from './communityService';
import appDataService, { Club, Pub, Shop, Series, Address, League, Association } from './appDataService';
import tournamentService, { Tournament } from './tournamentService';
import { t as tr } from '../i18n';

// ============================================
// ListingService – Port der Flutter ListingService.getRecentEntries
//
// Lädt die neuesten Einträge (Turniere, Vereine, Spielstätten, Shops,
// Turnierserien) client-seitig zusammen, filtert auf Einträge mit Bild,
// sortiert nach createdAt absteigend und liefert eine gemischte Liste.
//
// WICHTIG: Alles defensiv – Felder können fehlen oder null sein.
// ============================================

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

export type ListingType = 'tournament' | 'club' | 'pub' | 'shop' | 'series' | 'league' | 'association';

/** Listing-Typen, die gefolgt werden können (exakte Backend-Werte, uppercase). */
export type FollowListingType = 'CLUB' | 'PUB' | 'SHOP' | 'LEAGUE' | 'ASSOCIATION';

/** Listing-Typen, die dupliziert werden können (exakte Backend-Werte, uppercase). */
export type DuplicateListingType = 'TOURNAMENT' | 'CLUB' | 'PUB' | 'SHOP' | 'SERIES' | 'LEAGUE' | 'ASSOCIATION';

export interface DuplicateListingResponse {
  success: boolean;
  id: string | null;
  slug: string | null;
  type: string | null;
}

export interface ListingFollowActionResponse {
  success: boolean;
  following: boolean | null;
  followerCount: number | null;
}

export interface ListingFollower {
  userId: string;
  username: string;
  image: string | null;
}

export interface ListingFollowersResponse {
  followers: ListingFollower[];
  total: number;
}

export interface ListingEntry {
  id: string;
  slug: string;
  name: string;
  type: ListingType;
  image: string | null;
  images: string[] | null;
  logo: string | null;
  location: string;
  createdAt: number;
  cancelled: boolean;
  /** Beschreibung des Eintrags (nullable — für Karten-Vorschauen) */
  description: string | null;
}

const buildFileUrl = (fileName?: string | null): string | null => {
  if (!fileName || typeof fileName !== 'string' || !fileName.trim()) return null;
  if (fileName.startsWith('data:')) return fileName;
  if (fileName.startsWith('http://') || fileName.startsWith('https://')) return fileName;
  return `${API_BASE_URL}/api/file/${fileName}`;
};

const firstImage = (image?: string | null, images?: string[] | null): string | null => {
  if (image && image.trim()) return image;
  if (images && Array.isArray(images) && images.length > 0 && images[0]?.trim()) {
    return images[0];
  }
  return null;
};

const locationLabel = (address?: Address): string => {
  if (!address) return tr('web.component.comment-item.unbekannt');
  if (address.label && address.label.trim()) return address.label.trim();
  const city = [address.zipCode, address.city].filter(Boolean).join(' ').trim();
  if (city) return city;
  if (address.country && address.country.trim()) return address.country.trim();
  return tr('web.component.comment-item.unbekannt');
};

const idOf = (item: { _id?: string; id?: string }): string =>
  item.id || item._id || '';

class ListingService {
  // --------------------------------------------
  // Listing-Follows (Vereine, Spielstätten, Shops)
  // --------------------------------------------

  /**
   * POST /api/listing/follow (auth)
   * Folgt einem Listing (CLUB | PUB | SHOP). Wirft bei Fehlern.
   */
  async followListing(type: FollowListingType, id: string): Promise<ListingFollowActionResponse> {
    const res = await api.post('/api/listing/follow', { type, id });
    void firstPartyAnalytics.trackEvent(AnalyticsEvents.listingFollow, { content_type: String(type).toLowerCase(), content_id: id });
    return this.parseFollowActionResponse(res?.data);
  }

  /**
   * POST /api/listing/unfollow (auth)
   * Entfolgt einem Listing (CLUB | PUB | SHOP). Wirft bei Fehlern.
   */
  async unfollowListing(type: FollowListingType, id: string): Promise<ListingFollowActionResponse> {
    const res = await api.post('/api/listing/unfollow', { type, id });
    void firstPartyAnalytics.trackEvent(AnalyticsEvents.listingUnfollow, { content_type: String(type).toLowerCase(), content_id: id });
    return this.parseFollowActionResponse(res?.data);
  }

  /**
   * GET /api/listing/:id/followers?limit&offset (nur Owner/Admin)
   * Lädt die Follower-Liste eines Listings. Fehler → leere Liste.
   */
  async getListingFollowers(
    id: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<ListingFollowersResponse> {
    const { limit = 100, offset = 0 } = params;
    try {
      const query = new URLSearchParams();
      query.set('limit', String(limit));
      query.set('offset', String(offset));
      const res = await api.get(
        `/api/listing/${encodeURIComponent(id)}/followers?${query.toString()}`,
      );
      const source = res?.data?.data ?? res?.data ?? {};
      const raw = Array.isArray(source?.followers) ? source.followers : [];
      const followers: ListingFollower[] = raw
        .filter(
          (f: any) =>
            f &&
            typeof f === 'object' &&
            typeof f.userId === 'string' &&
            f.userId.length > 0 &&
            typeof f.username === 'string' &&
            f.username.length > 0,
        )
        .map((f: any) => ({
          userId: f.userId,
          username: f.username,
          image: typeof f.image === 'string' && f.image.length > 0 ? f.image : null,
        }));
      return {
        followers,
        total: typeof source?.total === 'number' ? source.total : followers.length,
      };
    } catch (error) {
      console.error('Fehler beim Abrufen der Listing-Follower:', error);
      return { followers: [], total: 0 };
    }
  }

  /**
   * GET /api/listing/:id/followers/public?limit=&offset= (NEU 2026-09-01)
   * Öffentliche Follower-Liste eines Listings — EXAKT dasselbe Format wie
   * die Profil-Follower-Liste: { profiles, total, privateCount }.
   * Fail-soft: Fehler/404 (altes Backend) → null (Aufrufer zeigt dezenten
   * Hinweis statt zu crashen). ALLE Felder defensiv geparst.
   */
  async getListingPublicFollowers(
    id: string,
    params: { limit?: number; offset?: number } = {},
  ): Promise<FollowListResponse | null> {
    const { limit = 30, offset = 0 } = params;
    if (!id || id.trim().length === 0) return null;
    try {
      const query = new URLSearchParams();
      query.set('limit', String(limit));
      query.set('offset', String(offset));
      const res = await api.get(
        `/api/listing/${encodeURIComponent(id)}/followers/public?${query.toString()}`,
      );
      const source = res?.data?.data ?? res?.data ?? {};
      const profilesRaw = Array.isArray(source?.profiles) ? source.profiles : [];
      const profiles: FollowListProfile[] = profilesRaw
        .filter(
          (p: any) =>
            p && typeof p === 'object' && typeof p.username === 'string' && p.username.length > 0,
        )
        .map((p: any) => ({
          username: p.username,
          image: typeof p.image === 'string' && p.image.length > 0 ? p.image : null,
          displayName:
            typeof p.displayName === 'string' && p.displayName.trim().length > 0
              ? p.displayName
              : null,
        }));
      return {
        profiles,
        total: typeof source?.total === 'number' ? source.total : profiles.length,
        privateCount: typeof source?.privateCount === 'number' ? source.privateCount : 0,
      };
    } catch (error) {
      console.error('Fehler beim Abrufen der öffentlichen Listing-Follower:', error);
      return null;
    }
  }

  /**
   * POST /api/listing/duplicate (auth)
   * Dupliziert ein Listing als Entwurf (name "<Name> (Entwurf)", visible=false,
   * bei Turnieren inkl. Competitions, ohne Anmeldungen). Wirft bei Fehlern
   * (403/404/Netz), damit die UI die Servermeldung anzeigen kann.
   */
  async duplicateListing(
    type: DuplicateListingType,
    id: string,
  ): Promise<DuplicateListingResponse> {
    const res = await api.post('/api/listing/duplicate', { type, id });
    const source = res?.data?.data ?? res?.data ?? {};
    return {
      success: source?.success === true,
      id: typeof source?.id === 'string' && source.id ? source.id : null,
      slug: typeof source?.slug === 'string' && source.slug ? source.slug : null,
      type: typeof source?.type === 'string' && source.type ? source.type : null,
    };
  }

  private parseFollowActionResponse(payload: any): ListingFollowActionResponse {
    const source = payload?.data ?? payload ?? {};
    return {
      success: source?.success === true,
      following: typeof source?.following === 'boolean' ? source.following : null,
      followerCount: typeof source?.followerCount === 'number' ? source.followerCount : null,
    };
  }

  private tournamentToEntry(t: Tournament): ListingEntry | null {
    const raw = firstImage(t.image, t.images);
    const image = buildFileUrl(raw) ?? tournamentService.getImageUrl(t);
    if (!image) return null;
    return {
      id: idOf(t),
      slug: t.slug || idOf(t),
      name: t.name || tr('web.component.create-entry-modal.turnier'),
      type: 'tournament',
      image,
      images: (t.images && t.images.length > 0 ? t.images.map((i) => buildFileUrl(i) || i) : null),
      logo: null,
      location: locationLabel(t.address),
      createdAt: t.createdAt || 0,
      cancelled: Boolean(t.cancelled),
      description: typeof t.description === 'string' ? t.description : null,
    };
  }

  private listingToEntry(
    item: Club | Pub | Shop | Series | League | Association,
    type: ListingType,
  ): ListingEntry | null {
    const raw = firstImage(item.image, item.images);
    const image = buildFileUrl(raw);
    const logo = 'logo' in item ? buildFileUrl((item as Club).logo) : null;
    // Wie in der Flutter-App (hasImage): Logo zählt als Bild. Nur Einträge
    // ganz ohne Bildmaterial werden ausgeblendet.
    if (!image && !logo) return null;
    return {
      id: idOf(item),
      slug: item.slug || idOf(item),
      name: item.name || '',
      type,
      image,
      images: item.images && item.images.length > 0 ? item.images.map((i) => buildFileUrl(i) || i) : null,
      logo,
      location: locationLabel(item.address),
      createdAt: item.createdAt || 0,
      cancelled: false,
      description: typeof item.description === 'string' ? item.description : null,
    };
  }

  /** Lädt die neuesten Einträge aller Typen (mit Bild), sortiert nach Datum. */
  async getRecentEntries(limit = 20): Promise<ListingEntry[]> {
    const entries: ListingEntry[] = [];

    const [tournamentsRes, clubs, pubs, shops, series, leagues, associations] = await Promise.all([
      // 2026-10-06: sortiert vom Server (zuletzt angelegt zuerst) – limit=500 ohne Sortierung lieferte die 500 ÄLTESTEN,
      // bei > 500 Turnieren fehlten „Neu bei DartCloud“ genau die neuesten Turniere
      tournamentService.getTournaments({ limit: 60, sort: 'newest' }).catch(() => null),
      appDataService.getClubs().catch(() => [] as Club[]),
      appDataService.getPubs().catch(() => [] as Pub[]),
      appDataService.getShops().catch(() => [] as Shop[]),
      appDataService.getSeries().catch(() => [] as Series[]),
      appDataService.getLeagues().catch(() => [] as League[]),
      appDataService.getAssociations().catch(() => [] as Association[]),
    ]);

    const tournaments = tournamentsRes?.data ?? [];
    for (const t of tournaments) {
      const e = this.tournamentToEntry(t);
      if (e) entries.push(e);
    }
    for (const c of clubs) {
      const e = this.listingToEntry(c, 'club');
      if (e) entries.push(e);
    }
    for (const p of pubs) {
      const e = this.listingToEntry(p, 'pub');
      if (e) entries.push(e);
    }
    for (const s of shops) {
      const e = this.listingToEntry(s, 'shop');
      if (e) entries.push(e);
    }
    for (const s of series) {
      const e = this.listingToEntry(s, 'series');
      if (e) entries.push(e);
    }
    for (const l of leagues) {
      const e = this.listingToEntry(l, 'league');
      if (e) entries.push(e);
    }
    for (const a of associations) {
      const e = this.listingToEntry(a, 'association');
      if (e) entries.push(e);
    }

    entries.sort((a, b) => b.createdAt - a.createdAt);
    return entries.slice(0, limit);
  }

  /** Ziel-Pfad für einen Eintrag (deutsche URL-Pfade wie in App.tsx). */
  routeFor(entry: ListingEntry): string | null {
    if (!entry.slug) return null;
    switch (entry.type) {
      case 'tournament':
        return `/turnier/${entry.slug}`;
      case 'club':
        return `/verein/${entry.slug}`;
      case 'pub':
        return `/spielstaette/${entry.slug}`;
      case 'shop':
        return `/shop/${entry.slug}`;
      case 'series':
        return `/turnierserie/${entry.slug}`;
      case 'league':
        return `/liga/${entry.slug}`;
      case 'association':
        return `/verband/${entry.slug}`;
      default:
        return null;
    }
  }
}

const listingService = new ListingService();
export default listingService;
